// frontend/src/services/userSettingsService.js

/**
 * Сервіс для управління налаштуваннями користувача та cookies
 * Забезпечує збереження імен у кімнатах та управління згодою на cookies
 */
import clientLogger from './clientLogger';

class UserSettingsService {
    constructor() {
        // База API визначається так:
        // 1) Якщо задано REACT_APP_API_BASE — використовуємо її (рекомендовано для Vercel → Render)
        // 2) Інакше в production — поточний origin (коли фронт і бек на одному домені)
        // 3) У dev — http://<host>:10000
        const envApi = process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.trim();
        if (envApi) {
            this.apiBase = envApi.replace(/\/$/, '');
        } else if (process.env.NODE_ENV === 'production') {
            this.apiBase = window.location.origin;
        } else {
            const protocol = window.location.protocol || 'http:';
            const hostname = window.location.hostname || 'localhost';
            const port = 10000; // бекенд у dev
            this.apiBase = `${protocol}//${hostname}:${port}`;
        }
        
        // Локальний кеш для мінімізації запитів
        this.cache = {
            displayName: new Map(),
            consent: null,
            lastUpdate: null
        };
        try { clientLogger.info('UserSettingsService init', { apiBase: this.apiBase }, 'COOKIE'); } catch {}
    }

    /**
     * Збереження імені користувача для кімнати
     * @param {string} displayName - Відображуване ім'я
     * @param {string} roomId - ID кімнати (опціонально)
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async saveDisplayName(displayName, roomId = null) {
        try {
            try { clientLogger.info('saveDisplayName request', { displayName, roomId }, 'COOKIE'); } catch {}
            const response = await fetch(`${this.apiBase}/api/user/display-name`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Важливо для відправки cookies
                body: JSON.stringify({
                    displayName: displayName.trim(),
                    roomId
                })
            });

            // Перевіряємо статус відповіді перед спробою розпарсити JSON
            if (!response.ok) {
                if (response.status === 405) {
                    console.warn('⚠️ [USER-SETTINGS] API недоступне (405), використовуємо локальне сховище');
                    this.setLocalDisplayName(displayName, roomId);
                    return { success: true, message: 'Збережено локально' };
                }
                throw new Error(`HTTP ${response.status}`);
            }

            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.warn('❌ [USER-SETTINGS] Помилка парсингу JSON відповіді:', parseError);
                throw new Error('Invalid JSON response');
            }
            try { clientLogger.info('saveDisplayName response', { ok: response.ok, status: response.status, result }, 'COOKIE'); } catch {}
            
            if (result.success) {
                // Оновлюємо локальний кеш
                const cacheKey = roomId || 'global';
                this.cache.displayName.set(cacheKey, displayName.trim());
                console.log('✅ [USER-SETTINGS] Ім\'я збережено:', displayName);
                return result;
            }

            // Якщо сервер повернув помилку, використовуємо fallback
            console.warn('⚠️ [USER-SETTINGS] Сервер повернув помилку, використовуємо локальне сховище');
            this.setLocalDisplayName(displayName, roomId);
            return { success: true, message: 'Збережено локально' };

        } catch (error) {
            console.error('❌ [USER-SETTINGS] Помилка збереження імені:', error);
            try { clientLogger.error('saveDisplayName failed', { error: String(error) }, 'COOKIE'); } catch {}
            
            // Fallback: зберігаємо локально
            this.setLocalDisplayName(displayName, roomId);
            return {
                success: true, 
                message: 'Збережено локально (сервер недоступний)',
                fallback: true
            };
        }
    }

    /**
     * Отримання збереженого імені користувача
     * @param {string} roomId - ID кімнати (опціонально)
     * @returns {Promise<string|null>}
     */
    async getDisplayName(roomId = null) {
        try {
            // Перевіряємо кеш
            const cacheKey = roomId || 'global';
            const cached = this.cache.displayName.get(cacheKey);
            if (cached) {
                return cached;
            }

            // Спробуємо отримати з сервера
            const queryParams = roomId ? `?roomId=${encodeURIComponent(roomId)}` : '';
            try { clientLogger.info('getDisplayName request', { roomId }, 'COOKIE'); } catch {}
            
            const response = await fetch(`${this.apiBase}/api/user/display-name${queryParams}`, {
                method: 'GET',
                credentials: 'include'
            });

            // Якщо API недоступне, використовуємо локальне сховище
            if (!response.ok) {
                if (response.status === 405) {
                    console.warn('⚠️ [USER-SETTINGS] API недоступне (405), використовуємо локальне сховище');
                    return this.getLocalDisplayName(roomId);
                }
                throw new Error(`HTTP ${response.status}`);
            }

            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.warn('❌ [USER-SETTINGS] Помилка парсингу JSON відповіді:', parseError);
                throw new Error('Invalid JSON response');
            }
            try { clientLogger.info('getDisplayName response', { ok: response.ok, status: response.status, result }, 'COOKIE'); } catch {}
            
            // Обробляємо різні формати відповідей
            let displayName = null;
            if (result.success && result.displayName) {
                displayName = result.displayName;
            } else if (result.name) {
                displayName = result.name;
            }

            if (displayName) {
                // Оновлюємо кеш
                this.cache.displayName.set(cacheKey, displayName);
                console.log('📖 [USER-SETTINGS] Отримано збережене ім\'я:', displayName);
                return displayName;
            }

            // Якщо з сервера нічого не отримали, спробуємо локальне сховище
            return this.getLocalDisplayName(roomId);
            
        } catch (error) {
            console.error('❌ [USER-SETTINGS] Помилка отримання імені:', error);
            try { clientLogger.error('getDisplayName failed', { error: String(error) }, 'COOKIE'); } catch {}
            
            // Fallback: спробуємо локальне сховище
            return this.getLocalDisplayName(roomId);
        }
    }

    /**
     * Встановлення згоди користувача на cookies
     * @param {Object} consent - Об'єкт згоди {functional, analytics, marketing}
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async setUserConsent(consent) {
        try {
            try { clientLogger.info('setUserConsent request', { consent }, 'COOKIE'); } catch {}
            const response = await fetch(`${this.apiBase}/api/user/consent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(consent)
            });

            // Перевіряємо статус відповіді
            if (!response.ok) {
                if (response.status === 405) {
                    console.warn('⚠️ [USER-SETTINGS] API недоступне (405), зберігаємо згоду локально');
                    this.setLocalConsent(consent);
                    return { success: true, message: 'Згода збережена локально' };
                }
                throw new Error(`HTTP ${response.status}`);
            }

            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.warn('❌ [USER-SETTINGS] Помилка парсингу JSON відповіді:', parseError);
                throw new Error('Invalid JSON response');
            }
            try { clientLogger.info('setUserConsent response', { ok: response.ok, status: response.status, result }, 'COOKIE'); } catch {}
            
            if (result.success) {
                // Оновлюємо кеш
                this.cache.consent = result.consent || consent;
                this.cache.lastUpdate = Date.now();
                console.log('✅ [USER-SETTINGS] Згода збережена:', result.consent || consent);
                return result;
            }

            // Fallback до локального збереження
            this.setLocalConsent(consent);
            return { success: true, message: 'Згода збережена локально', fallback: true };

        } catch (error) {
            console.error('❌ [USER-SETTINGS] Помилка збереження згоди:', error);
            try { clientLogger.error('setUserConsent failed', { error: String(error) }, 'COOKIE'); } catch {}
            
            // Fallback: зберігаємо локально
            this.setLocalConsent(consent);
            return {
                success: true,
                message: 'Згода збережена локально (сервер недоступний)',
                fallback: true
            };
        }
    }

    /**
     * Отримання згоди користувача
     * @returns {Promise<Object|null>}
     */
    async getUserConsent() {
        try {
            // Перевіряємо кеш (5 хвилин)
            if (this.cache.consent && this.cache.lastUpdate && 
                (Date.now() - this.cache.lastUpdate) < 5 * 60 * 1000) {
                return this.cache.consent;
            }

            // Спробуємо отримати з сервера
            try { clientLogger.info('getUserConsent request', {}, 'COOKIE'); } catch {}
            const response = await fetch(`${this.apiBase}/api/user/consent`, {
                method: 'GET',
                credentials: 'include'
            });

            // Якщо API недоступне, використовуємо локальне сховище
            if (!response.ok) {
                if (response.status === 405) {
                    console.warn('⚠️ [USER-SETTINGS] API недоступне (405), використовуємо локальне сховище');
                    return this.getLocalConsent();
                }
                throw new Error(`HTTP ${response.status}`);
            }

            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.warn('❌ [USER-SETTINGS] Помилка парсингу JSON відповіді:', parseError);
                throw new Error('Invalid JSON response');
            }
            try { clientLogger.info('getUserConsent response', { ok: response.ok, status: response.status, result }, 'COOKIE'); } catch {}
            
            // Обробляємо різні формати відповідей
            let consent = null;
            if (result.success && result.consent) {
                consent = result.consent;
            } else if (result.consent) {
                consent = result.consent;
            }

            if (consent) {
                this.cache.consent = consent;
                this.cache.lastUpdate = Date.now();
                return consent;
            }

            // Fallback до локального сховища
            return this.getLocalConsent();
            
        } catch (error) {
            console.error('❌ [USER-SETTINGS] Помилка отримання згоди:', error);
            try { clientLogger.error('getUserConsent failed', { error: String(error) }, 'COOKIE'); } catch {}
            
            // Fallback: спробуємо локальне сховище
            return this.getLocalConsent();
        }
    }

    /**
     * Очищення всіх cookies користувача
     * @param {Array<string>} cookieNames - Список імен cookies для видалення
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async clearUserCookies(cookieNames = []) {
        try {
            try { clientLogger.info('clearUserCookies request', { cookieNames }, 'COOKIE'); } catch {}
            const response = await fetch(`${this.apiBase}/api/user/cookies`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ cookieNames })
            });

            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.warn('❌ [USER-SETTINGS] Помилка парсингу JSON відповіді:', parseError);
                throw new Error('Invalid JSON response');
            }
            try { clientLogger.info('clearUserCookies response', { ok: response.ok, status: response.status, result }, 'COOKIE'); } catch {}
            
            if (result.success) {
                // Очищуємо локальний кеш
                this.cache.displayName.clear();
                this.cache.consent = null;
                this.cache.lastUpdate = null;
                console.log('🗑️ [USER-SETTINGS] Cookies очищено');
            }

            return result;
        } catch (error) {
            console.error('❌ [USER-SETTINGS] Помилка очищення cookies:', error);
            try { clientLogger.error('clearUserCookies failed', { error: String(error) }, 'COOKIE'); } catch {}
            return {
                success: false,
                error: 'Помилка мережі'
            };
        }
    }

    /**
     * Перевірка наявності згоди на функціональні cookies
     * @returns {Promise<boolean>}
     */
    async hasFunctionalConsent() {
        const consent = await this.getUserConsent();
        // Якщо згоди немає, вважаємо функціональні cookies дозволеними
        if (!consent) return true;
        return Boolean(consent.functional);
    }

    /**
     * Отримання збереженого імені з локального сховища як fallback
     * @param {string} roomId - ID кімнати
     * @returns {string|null}
     */
    getLocalDisplayName(roomId = null) {
        try {
            const key = roomId ? `room_display_name_${roomId}` : 'room_display_name';
            return localStorage.getItem(key);
        } catch (error) {
            console.warn('❌ [USER-SETTINGS] Помилка доступу до localStorage:', error);
            try { clientLogger.warn('localStorage get failed', { error: String(error) }, 'COOKIE'); } catch {}
            return null;
        }
    }

    /**
     * Збереження імені в локальному сховищі як fallback
     * @param {string} displayName - Відображуване ім'я
     * @param {string} roomId - ID кімнати
     */
    setLocalDisplayName(displayName, roomId = null) {
        try {
            const key = roomId ? `room_display_name_${roomId}` : 'room_display_name';
            localStorage.setItem(key, displayName.trim());
            console.log('💾 [USER-SETTINGS] Ім\'я збережено локально:', displayName);
        } catch (error) {
            console.warn('❌ [USER-SETTINGS] Помилка збереження в localStorage:', error);
            try { clientLogger.warn('localStorage set failed', { error: String(error) }, 'COOKIE'); } catch {}
        }
    }

    /**
     * Збереження згоди в локальному сховищі як fallback
     * @param {Object} consent - Об'єкт згоди
     */
    setLocalConsent(consent) {
        try {
            localStorage.setItem('user_consent', JSON.stringify(consent));
            this.cache.consent = consent;
            this.cache.lastUpdate = Date.now();
            console.log('💾 [USER-SETTINGS] Згода збережена локально:', consent);
        } catch (error) {
            console.warn('❌ [USER-SETTINGS] Помилка збереження згоди в localStorage:', error);
            try { clientLogger.warn('localStorage consent set failed', { error: String(error) }, 'COOKIE'); } catch {}
        }
    }

    /**
     * Отримання згоди з локального сховища як fallback
     * @returns {Object|null}
     */
    getLocalConsent() {
        try {
            const stored = localStorage.getItem('user_consent');
            if (stored) {
                const consent = JSON.parse(stored);
                this.cache.consent = consent;
                this.cache.lastUpdate = Date.now();
                return consent;
            }
            return null;
        } catch (error) {
            console.warn('❌ [USER-SETTINGS] Помилка отримання згоди з localStorage:', error);
            try { clientLogger.warn('localStorage consent get failed', { error: String(error) }, 'COOKIE'); } catch {}
            return null;
        }
    }

    /**
     * Очищення кешу
     */
    clearCache() {
        this.cache.displayName.clear();
        this.cache.consent = null;
        this.cache.lastUpdate = null;
    }
}

// Експортуємо синглтон
export default new UserSettingsService();
